import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import dynamoService from '../services/dynamoService';
import './BlockedMessages.css';

const ScoredFeedback = () => {
  const { isDarkMode } = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    load(true);
    const interval = setInterval(() => load(false), 15000);
    return () => clearInterval(interval);
  }, []);

  const load = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const result = await dynamoService.getScoredFeedback();
      if (result.success) {
        const getScore = (x) => (x && x.score !== undefined ? x.score : (x && x.metrics && x.metrics.score) || 0);
        const sorted = (result.items || []).sort((a, b) => {
          const scoreA = getScore(a);
          const scoreB = getScore(b);
          // Descending by score
          if (scoreB !== scoreA) return scoreB - scoreA;
          // Tiebreaker by timestamp desc
          const timeA = new Date(a.timestamp || a.scoredAt || a.createdAt || 0).getTime();
          const timeB = new Date(b.timestamp || b.scoredAt || b.createdAt || 0).getTime();
          return timeB - timeA;
        });
        setItems(sorted);
      } else {
        setError(result.error);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString();
  };

  const maskEmail = (email) => {
    if (!email) return 'N/A';
    const parts = String(email).split('@');
    if (parts.length !== 2) return email;
    const [localPart, domain] = parts;
    const maskedLocal = localPart.length > 2 
      ? localPart.substring(0, 2) + '*'.repeat(localPart.length - 2)
      : localPart;
    return `${maskedLocal}@${domain}`;
  };

  if (loading) {
    return (
      <div className={`blocked-messages scored ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading scored feedback...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`blocked-messages scored ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="error-container">
          <h2>Error Loading Scored Feedback</h2>
          <p className="error-message">{error}</p>
          <button onClick={() => load(true)} className="retry-button">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`blocked-messages scored ${isDarkMode ? 'dark' : 'light'}`}>
      <div className="blocked-header">
        <h1>Scored Feedback</h1>
        <p className="subtitle">Scores and feedback from flink-bedrock-demo-scored-feedback</p>
        <div className="stats">
          <span className="stat-item">
            <strong>{items.length}</strong> entries
          </span>
          <button onClick={() => load(true)} className="refresh-button">Refresh</button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="no-messages">
          <p>No scored feedback found.</p>
        </div>
      ) : (
        <div className="messages-table-container">
          <table className="messages-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Score</th>
                <th>Award</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Determine top 10 awards (by score) among entries that have a valid email
                let awardsGiven = 0;
                return items.map((item, index) => {
                  const emailRaw = (item.email || '').toString().trim();
                  const emailLower = emailRaw.toLowerCase();
                  const eligibleEmail = emailRaw && emailLower !== 'none' && emailLower !== 'n/a';
                  let awardText = '';
                  if (eligibleEmail && awardsGiven < 10) {
                    awardText = '25$ AWS Credit';
                    awardsGiven += 1;
                  }
                  const scoreValue = item && item.score !== undefined ? item.score : (item && item.metrics && item.metrics.score);
                  return (
                    <tr key={item.messageId || item.id || index}>
                      <td className="email" data-label="Email">{maskEmail(item.email)}</td>
                      <td className="email" data-label="Score">{scoreValue !== undefined ? scoreValue : 'N/A'}</td>
                      <td className="email" data-label="Award">{awardText}</td>
                      <td className="timestamp" data-label="Timestamp">{formatTimestamp(item.timestamp || item.scoredAt || item.createdAt)}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScoredFeedback;


